import { Button } from '@zendeskgarden/react-buttons'
import { Datepicker } from '@zendeskgarden/react-datepickers'
import { Item } from '@zendeskgarden/react-dropdowns'
import { Checkbox, Field, Input, Label, Radio } from '@zendeskgarden/react-forms'
import { Body, Close, Footer, FooterItem, Header, Modal } from '@zendeskgarden/react-modals'
import moment from 'moment'
import React, { useEffect, useState } from 'react'

import { useCurrentDate } from '../../hooks/useCurrentDate'
import { RecordFactory } from '../../models/RecordFactory'
import { Template } from '../../models/Template'
import { Spinner } from '../Spinner'
import { PSDropdown } from '../app/PSDropdown'
import { ItemOption } from '../settings/SetupBusinessHoursModalV1'

const laterHours: (ItemOption & { date: Date })[] = []
for (let i = 0; i < 24; i++) {
    laterHours.push({
        value: i,
        name: moment().hour(i).minute(0).format('h:mma'),
        date: moment().hour(i).minute(0).toDate(),
    })
    laterHours.push({
        value: i + 0.5,
        name: moment().hour(i).minute(30).format('h:mma'),
        date: moment().hour(i).minute(30).toDate(),
    })
}
laterHours.push({
    value: 24,
    name: moment().hour(0).minute(0).format('h:mma'),
    date: moment().hour(0).minute(0).toDate(),
})

type Props = {
    onClose: () => void
    onSend: (message: string, templateID: string, sendAt?: Date) => void
}

export const TemplateModal: React.FC<Props> = ({ onClose, onSend }) => {
    const [isLoading, setIsLoading] = useState(true)
    const [sendEnabled, setSendEnabled] = useState(false)
    const [sendLater, setSendLater] = useState(false)
    const [laterDate, setLaterDate] = useState(new Date())
    const [laterTime, setLaterTime] = useState<ItemOption>()
    const [selectedMessage, setSelectedMessage] = useState<Template>()
    const [savedMessages, setSavedMessages] = useState<Template[]>([])

    useEffect(() => {
        const fetchSavedMessages = async () => {
            setIsLoading(true)
            setSavedMessages(await RecordFactory.getInstance().loadTemplates())
            setIsLoading(false)
        }

        fetchSavedMessages()
    }, [])

    useEffect(() => {
        if (sendLater) {
            if (!laterTime) {
                setSendEnabled(false)
                return
            }

            const sendAt = moment(laterDate)
                .hour(Number(laterTime.value))
                .minute(laterTime.value.toString().endsWith('.5') ? 30 : 0)
            setSendEnabled(sendAt.isAfter(moment()) && !!selectedMessage)
        }
        else {
            setSendEnabled(!!selectedMessage)
        }
    }, [sendLater, laterTime, selectedMessage, laterDate])

    const onSendClicked = () => {
        if (!selectedMessage) return

        let sendAt: Date | undefined
        if (sendLater && laterTime) {
            sendAt = moment(laterDate)
                .hour(Number(laterTime.value))
                .minute(laterTime.value.toString().endsWith('.5') ? 30 : 0)
                .toDate()
        }

        onSend(selectedMessage.message || '', selectedMessage.id || '', sendAt)
    }

    const currentDate = useCurrentDate()
    const futureLaterHours = laterHours.filter((hour) => moment(hour.date).isAfter(currentDate))

    return (
        <Modal className="savedMessagesModal" onClose={onClose}>
            <Header>Select a saved message</Header>
            <Body>
                {isLoading && <Spinner />}
                {!isLoading && (
                    <>
                        <div className="details">
                            Select send and clients will be sent the message right away. If you'd
                            like to schedule the message to go out later tap "Send later" to
                            schedule when your message will be sent.
                        </div>
                        <Field className="field">
                            <div className="check">
                                <Checkbox
                                    checked={sendLater}
                                    onChange={(e) => setSendLater(e.target.checked)}
                                >
                                    <Label>Send later</Label>
                                </Checkbox>
                            </div>
                            <div className="sendLaterSelections">
                                <div className="slimField">
                                    <Datepicker
                                        value={sendLater ? laterDate : undefined}
                                        onChange={setLaterDate}
                                        minValue={new Date()}
                                    >
                                        <Input disabled={!sendLater} />
                                    </Datepicker>
                                </div>
                                <div className="slimFieldTime" style={{ margin: '0 24px 0 12px' }}>
                                    <PSDropdown
                                        selected={sendLater ? laterTime : undefined}
                                        nameProperty="name"
                                        onSelect={setLaterTime}
                                        label=""
                                        disabled={!sendLater}
                                    >
                                        <>
                                            {futureLaterHours.map((option) => (
                                                <Item key={`later-${option.value}`} value={option}>
                                                    {option.name}
                                                </Item>
                                            ))}
                                        </>
                                    </PSDropdown>
                                </div>
                            </div>
                        </Field>
                        {savedMessages.map((savedMessage: Template) => (
                            <Field
                                className="messageOption"
                                key={`savedmessage-${savedMessage.id}`}
                            >
                                <Radio
                                    name="savedmessage radio"
                                    value={savedMessage.id}
                                    checked={
                                        selectedMessage
                                            ? selectedMessage.id === savedMessage.id
                                            : false
                                    }
                                    onChange={() => setSelectedMessage(savedMessage)}
                                >
                                    <Label>{savedMessage.title}</Label>
                                    <div className="messageBody">{savedMessage.message}</div>
                                </Radio>
                            </Field>
                        ))}
                    </>
                )}
            </Body>
            <Footer style={{ paddingBottom: 32 }}>
                <FooterItem>
                    <Button onClick={onSendClicked} disabled={!sendEnabled} isPrimary>
                        Send
                    </Button>
                </FooterItem>
            </Footer>
            <Close aria-label="Close modal" />
        </Modal>
    )
}
